import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProduccionSkuPicker from './ProduccionSkuPicker';
import BarcodeCamera from './BarcodeCamera';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { parseKg, formatKg, formatDate, parseLocation } from '../utils/produccion';
import useWideLayout from '../utils/useWideLayout';
import '../styles/produccion.css';

const DEBOUNCE_MS = 400;
// Same threshold the server uses to ask for confirmation.
const MERMA_LIMIT = 0.25;
const NO_FLAGS = { confirm_expiration: false, confirm_merma: false };
const FLAG_FOR = { confirmar_vencimiento: 'confirm_expiration', confirmar_merma: 'confirm_merma' };
const NOT_READY = {
  por_retirar: 'Aún falta retirar materia prima.',
  retirando: 'Aún falta retirar materia prima.',
  terminada: 'Esta orden ya está terminada.',
  finalizada: 'Esta orden ya está cerrada.',
  anulada: 'Esta orden está anulada.',
  antigua: 'Las órdenes antiguas no se terminan desde esta pantalla.',
};
// Bolsa, interfolio y etiqueta: information only, they move no stock.
const COUNTS = [
  { key: 'bolsa', label: 'Bolsa' },
  { key: 'interfolio', label: 'Interfolio' },
  { key: 'etiqueta', label: 'Etiqueta' },
];
const BAD_COUNT = 'Bolsa, interfolio y etiqueta van en números enteros.';
const countValue = (text) => (String(text).trim() === '' ? null : Number(String(text).trim()));
const BAD_LOCATION = 'Ubicación no válida. Escríbela como A-3.';
const WHOLE_UNITS = 'Escribe las unidades sin puntos ni comas.';
const CAMERA_ERROR = 'No se pudo abrir la cámara. Usa la pistola o escribe el código.';

const round3 = (n) => Math.round(n * 1000) / 1000;
// One decimal, halves away from zero like the server: 13,3 / -8,3 / 100 (+ 0 turns -0 into 0).
const round1 = (n) => (Math.sign(n) * Math.round(Math.abs(n) * 10)) / 10 + 0;
const placeOf = (lot) => `${lot.rack}-${lot.shelf}`;
const expiry = (date) => (date ? `vence ${formatDate(date)}` : 'sin vencimiento');

// Leftover kg: empty is 0, "8,4" and "8.4" both work; anything else is null.
function parseAmount(text) {
  const clean = String(text === null || text === undefined ? '' : text).trim().replace(',', '.');
  if (clean === '') return 0;
  if (!/^\d*\.?\d+$|^\d+\.$/.test(clean)) return null;
  return Number(clean);
}

// Local date `days` from today as YYYY-MM-DD (what <input type="date"> gives).
function isoDay(days = 0) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Units are digits only: in es-CL "1.200" means 1200, but parseKg would read 1,2.
const unitsText = (text) => String(text || '').trim();
const badUnits = (text) => unitsText(text) !== '' && !/^\d+$/.test(unitsText(text));

// A whole number of units > 0, else null.
function parseUnits(text) {
  return !badUnits(text) && Number(unitsText(text)) > 0 ? Number(unitsText(text)) : null;
}

// "PT-2" -> {rack, shelf}. Two scans run together ("PT-4PT-5") leave a dash in
// the shelf: refused, or the product would be stored on a shelf that doesn't exist.
function locationOf(text) {
  const place = parseLocation(text);
  if (!place || !place.rack.trim() || !place.shelf.trim() || place.shelf.includes('-')) return null;
  return place;
}

// Why an output line can't be sent yet ('' when it can).
function outputProblem(out, today) {
  if (out.loading) return 'Cargando datos del producto…';
  if (!out.unit) return 'Elige si se cuenta en unidades o en kg';
  if (out.unit === 'un' && badUnits(out.units)) return WHOLE_UNITS;
  if (out.unit === 'un' && parseUnits(out.units) === null) return 'Faltan las unidades';
  if (parseKg(out.weight) === null) return 'Falta el peso total';
  if (!out.expiration) return 'Falta el vencimiento';
  if (out.expiration <= today) return 'El vencimiento debe ser posterior a hoy';
  if (!out.location.trim()) return 'Falta la ubicación';
  if (!locationOf(out.location)) return BAD_LOCATION;
  return '';
}

function supplyProblem(supply) {
  if (parseKg(supply.qty) === null) return 'Falta la cantidad de un insumo';
  if (!supply.preview || supply.preview.loading) return 'Calculando stock…';
  if (supply.preview.error || !supply.preview.data.enough) return 'Falta stock de un insumo';
  return '';
}

function leftoverProblem(row, text) {
  const kg = parseAmount(text);
  if (kg === null || kg > Number(row.pulled)) {
    return `El devuelto de ${row.sku.name} debe estar entre 0 y ${formatKg(row.pulled)} kg.`;
  }
  return '';
}

// Locations holding usable stock of a supply, for the "start from" select.
function placesOf(lots) {
  const byPlace = new Map();
  (lots || []).filter((lot) => !lot.expired).forEach((lot) => {
    const place = placeOf(lot);
    const row = byPlace.get(place) || { place, rack: lot.rack, shelf: lot.shelf, quantity: 0 };
    row.quantity += Number(lot.quantity);
    byPlace.set(place, row);
  });
  return [...byPlace.values()].sort((a, b) => a.place.localeCompare(b.place, undefined, { numeric: true }));
}

// "C-1 (100), C-2 (4)": where the close will deduct, in order.
function takenFrom(lots) {
  const byPlace = new Map();
  lots.filter((lot) => !lot.expired && Number(lot.use) > 0).forEach((lot) => {
    const place = placeOf(lot);
    byPlace.set(place, round3((byPlace.get(place) || 0) + Number(lot.use)));
  });
  return [...byPlace.entries()].map(([place, kg]) => `${place} (${formatKg(kg)})`).join(', ');
}

// Raw lots pulled for the order, one row per SKU and batch.
function madeWith(pulls) {
  const rows = new Map();
  (pulls || []).forEach((pull) => {
    const key = `${pull.sku.id}|${pull.batch}`;
    const row = rows.get(key) || { key, name: pull.sku.name, batch: pull.batch, date: pull.expiration_date, kg: 0 };
    row.kg = round3(row.kg + Number(pull.quantity) - Number(pull.returned_quantity || 0));
    rows.set(key, row);
  });
  return [...rows.values()].filter((row) => row.kg > 0);
}

// Typed, shot by the gun or read by the camera. The whole text is selected on
// focus and after every Enter, so the next gun scan replaces it instead of
// appending (Safari undoes the focus selection on click; the Enter one stays).
function LocationField({ label, value, onChange }) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  return (
    <>
      <div className="prod-field">
        <input
          type="text"
          aria-label={label}
          placeholder="A-3"
          autoComplete="off"
          value={value}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            e.target.select();
          }}
        />
        <button
          type="button"
          className="prod-scan-btn"
          aria-label="Escanear la ubicación con la cámara"
          onClick={() => { setCameraError(''); setCameraOpen((open) => !open); }}
        >
          📷
        </button>
      </div>
      {cameraOpen && (
        <div className="prod-camera">
          <BarcodeCamera
            onScan={(text) => {
              const code = String(text || '').trim();
              if (!code) return;
              setCameraOpen(false);
              onChange(code.toUpperCase());
            }}
            onError={() => { setCameraOpen(false); setCameraError(CAMERA_ERROR); }}
          />
        </div>
      )}
      {cameraError && <div className="prod-bad">{cameraError}</div>}
    </>
  );
}

function SupplyPreview({ supply, onStart }) {
  const { preview, places, start, sku } = supply;
  let body;
  if (!preview) body = <div className="prod-sub">Escribe la cantidad para ver de dónde se descuenta.</div>;
  else if (preview.loading) body = <div className="prod-sub">Calculando stock…</div>;
  else if (preview.error) body = <div className="prod-bad">{preview.error}</div>;
  else if (!preview.data.enough) body = <div className="prod-bad">{`Solo hay ${formatKg(preview.data.available)} en bodega.`}</div>;
  else body = <div className="prod-sub" style={{ margin: 0 }}>{`Se descuenta de: ${takenFrom(preview.data.lots)}`}</div>;
  return (
    <div className="prod-fefo">
      {body}
      {places.length > 1 && (
        <label className="prod-lbl" style={{ display: 'block' }}>
          Descontar primero de
          <select
            className="prod-input"
            aria-label={`Descontar primero de (${sku.name})`}
            value={start ? placeOf(start) : ''}
            onChange={(e) => onStart(e.target.value)}
          >
            <option value="">Automático (vence primero)</option>
            {places.map((p) => (
              <option key={p.place} value={p.place}>{`${p.place} · ${formatKg(p.quantity)}`}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

function ProduccionTerminar() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { po_number: poNumber } = useParams();
  const wide = useWideLayout();
  const orderPath = `orders/${encodeURIComponent(poNumber)}`;

  const [order, setOrder] = useState(null);
  const [raw, setRaw] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [outputs, setOutputs] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [leftovers, setLeftovers] = useState({});
  const [noOutput, setNoOutput] = useState(false);
  const [noOutputReason, setNoOutputReason] = useState('');
  const [counts, setCounts] = useState({ bolsa: '', interfolio: '', etiqueta: '' });
  const [addingOutput, setAddingOutput] = useState(false);
  const [addingSupply, setAddingSupply] = useState(false);
  const [error, setError] = useState(null); // {text, code}
  const [pending, setPending] = useState(null); // {code, message}: the server asks to confirm
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const busyRef = useRef(false);
  const flagsRef = useRef(NO_FLAGS);
  const counter = useRef(0);
  const timers = useRef({});
  const requests = useRef({});

  const patchOutput = (key, patch) => setOutputs((current) => current.map(
    (o) => (o.key === key ? { ...o, ...patch } : o),
  ));
  const patchSupply = (key, patch) => setSupplies((current) => current.map(
    (s) => (s.key === key ? { ...s, ...patch } : s),
  ));

  const loadHints = async (key, sku) => {
    const res = await produccionApi.get(`skus/${sku.id}/hints/`, token);
    const hints = res.ok && res.data
      ? res.data
      : { stock_unit: sku.stock_unit || '', suggested_unit: '', suggested_location: '' };
    const firstTime = !hints.stock_unit;
    setOutputs((current) => current.map((o) => (o.key !== key ? o : {
      ...o,
      loading: false,
      firstTime,
      suggested: firstTime ? hints.suggested_unit || '' : '',
      unit: o.unit || (firstTime ? hints.suggested_unit || '' : hints.stock_unit),
      location: o.location || hints.suggested_location || '',
    })));
  };

  const addOutput = (sku) => {
    counter.current += 1;
    const key = `o${counter.current}`;
    setOutputs((current) => [...current, {
      key, sku, loading: true, firstTime: false, suggested: '',
      unit: '', units: '', weight: '', expiration: '', location: '',
    }]);
    setAddingOutput(false);
    loadHints(key, sku);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [orderRes, infoRes] = await Promise.all([
        produccionApi.get(`${orderPath}/`, token),
        produccionApi.get(`${orderPath}/close-info/`, token),
      ]);
      if (cancelled) return;
      const failed = [orderRes, infoRes].find((res) => !res.ok);
      if (failed) {
        setLoadError(errorMessage(failed));
        return;
      }
      const rawRows = infoRes.data.raw || [];
      setRaw(rawRows);
      setLeftovers(Object.fromEntries(rawRows.map((row) => [row.sku.id, '0'])));
      setOrder(orderRes.data);
      // Every planned product comes ready as a line of what came out.
      const planned = orderRes.data.products || (orderRes.data.product ? [orderRes.data.product] : []);
      if (orderRes.data.stage === 'lista_para_terminar') planned.forEach(addOutput);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPath, token]);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const fetchSupply = async (key, skuId, qty, start) => {
    const request = (requests.current[key] || 0) + 1;
    requests.current[key] = request;
    const where = start ? `&rack=${encodeURIComponent(start.rack)}&shelf=${encodeURIComponent(start.shelf)}` : '';
    const res = await produccionApi.get(`fefo/?sku_id=${skuId}&quantity=${qty}&purpose=supply${where}`, token);
    if (requests.current[key] !== request) return;
    patchSupply(key, res.ok
      ? { preview: { data: res.data }, places: placesOf(res.data.lots) }
      : { preview: { error: errorMessage(res) } });
  };

  const scheduleSupply = (key, skuId, text, start, immediate) => {
    clearTimeout(timers.current[key]);
    requests.current[key] = (requests.current[key] || 0) + 1;
    const qty = parseKg(text);
    if (qty === null) {
      patchSupply(key, { preview: null });
      return;
    }
    patchSupply(key, { preview: { loading: true } });
    if (immediate) fetchSupply(key, skuId, qty, start);
    else timers.current[key] = setTimeout(() => fetchSupply(key, skuId, qty, start), DEBOUNCE_MS);
  };

  const addSupply = (sku) => {
    counter.current += 1;
    setSupplies((current) => [...current, {
      key: `s${counter.current}`, sku, qty: '', start: null, places: [], preview: null,
    }]);
    setAddingSupply(false);
  };

  const removeSupply = (key) => {
    clearTimeout(timers.current[key]);
    requests.current[key] = (requests.current[key] || 0) + 1;
    setSupplies((current) => current.filter((s) => s.key !== key));
  };

  const changeSupplyQty = (supply, text) => {
    patchSupply(supply.key, { qty: text });
    scheduleSupply(supply.key, supply.sku.id, text, supply.start, false);
  };

  const changeSupplyStart = (supply, place) => {
    const found = supply.places.find((p) => p.place === place);
    const start = found ? { rack: found.rack, shelf: found.shelf } : null;
    patchSupply(supply.key, { start });
    scheduleSupply(supply.key, supply.sku.id, supply.qty, start, true);
  };

  const today = isoDay(0);

  // Live merma, same formula as the server.
  const pulledKg = raw.reduce((sum, row) => sum + Number(row.pulled), 0);
  const leftoverKg = raw.reduce((sum, row) => sum + (parseAmount(leftovers[row.sku.id]) || 0), 0);
  const entered = round3(pulledKg - leftoverKg);
  const outKg = noOutput ? 0 : round3(outputs.reduce((sum, o) => sum + (parseKg(o.weight) || 0), 0));
  const merma = round3(entered - outKg) + 0; // + 0 turns -0 into 0
  const mermaText = entered > 0
    ? `Merma ${formatKg(merma)} kg · ${formatKg(round1((merma / entered) * 100))} %`
    : `Merma ${formatKg(merma)} kg`;
  const mermaAlert = !noOutput && entered > 0 && (merma < 0 || merma / entered > MERMA_LIMIT);

  // Why "Terminar producción" is off ('' when it can be pressed).
  let reason = '';
  if (raw.length === 0) reason = 'No se retiró materia prima. Anula la orden.';
  else if (noOutput) reason = noOutputReason.trim() ? '' : 'Escribe el motivo';
  else if (outputs.length === 0) reason = 'Agrega al menos un producto terminado';
  else reason = outputs.map((o) => outputProblem(o, today)).find(Boolean) || '';
  if (!reason) reason = raw.map((row) => leftoverProblem(row, leftovers[row.sku.id])).find(Boolean) || '';
  if (!reason) reason = supplies.map(supplyProblem).find(Boolean) || '';
  if (!reason && Object.values(counts).some((text) => text.trim() !== '' && !/^\d+$/.test(text.trim()))) {
    reason = BAD_COUNT;
  }

  const buildBody = () => ({
    outputs: noOutput ? [] : outputs.map((o) => {
      const place = locationOf(o.location) || { rack: '', shelf: '' };
      const row = {
        sku_id: o.sku.id,
        weight: parseKg(o.weight),
        expiration_date: o.expiration,
        rack: place.rack,
        shelf: place.shelf,
      };
      if (o.unit === 'un') row.quantity = parseUnits(o.units);
      if (o.firstTime) row.stock_unit = o.unit;
      return row;
    }),
    supplies: supplies.map((s) => {
      const row = { sku_id: s.sku.id, quantity: parseKg(s.qty) };
      if (s.start) {
        row.rack = s.start.rack;
        row.shelf = s.start.shelf;
      }
      return row;
    }),
    leftovers: raw
      .map((row) => ({ sku_id: row.sku.id, quantity: parseAmount(leftovers[row.sku.id]) }))
      .filter((row) => row.quantity > 0),
    no_output: noOutput,
    no_output_reason: noOutput ? noOutputReason.trim() : '',
    bolsa: countValue(counts.bolsa),
    interfolio: countValue(counts.interfolio),
    etiqueta: countValue(counts.etiqueta),
  });

  // A confirmation only covers the data it was asked about: any edit drops it.
  const bodyKey = JSON.stringify(buildBody());
  useEffect(() => {
    flagsRef.current = NO_FLAGS;
    setPending(null);
    setAck(false);
  }, [bodyKey]);

  const submit = async (flags) => {
    if (busyRef.current || reason) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setPending(null);
    setAck(false);
    const res = await produccionApi.send('POST', `${orderPath}/finish/`, { ...buildBody(), ...flags }, token);
    busyRef.current = false;
    setBusy(false);
    if (res.ok) {
      navigate(`/production/${poNumber}?terminada=1`);
      return;
    }
    const code = res.data && res.data.code;
    if (FLAG_FOR[code]) {
      flagsRef.current = flags;
      setPending({ code, message: res.data.error });
      return;
    }
    setError({ text: errorMessage(res), code });
  };

  const confirmAndClose = () => submit({ ...flagsRef.current, [FLAG_FOR[pending.code]]: true });

  const dropPending = () => {
    flagsRef.current = NO_FLAGS;
    setPending(null);
    setAck(false);
  };

  const header = (
    <div className="prod-retiro-head">
      <div>
        <h1 className="prod-title">Terminar producción</h1>
        <div className="prod-sub" style={{ margin: 0 }}>
          {order && order.product ? `${poNumber} · ${order.product.name}` : poNumber}
        </div>
      </div>
      <button type="button" className="prod-btn ghost prod-btn-small" onClick={() => navigate('/production')}>← Volver</button>
    </div>
  );

  if (loadError) {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-bad prod-error">{loadError}</div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-sub">Cargando…</div>
      </div>
    );
  }
  if (order.stage !== 'lista_para_terminar') {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-card amber">
          <div className="n">{NOT_READY[order.stage] || 'Esta orden no está lista para terminar.'}</div>
        </div>
        <button type="button" className="prod-btn blue" onClick={() => navigate('/production')}>Volver a Producción</button>
      </div>
    );
  }

  const renderOutput = (o) => {
    const { name } = o.sku;
    const badLocation = o.location.trim() !== '' && !locationOf(o.location);
    return (
      <div className="prod-line" key={o.key}>
        <div className="prod-line-head">
          <span>
            <b>{name}</b>
            {!o.loading && !o.firstTime && (
              <span className="prod-sub">{o.unit === 'kg' ? ' se cuenta en kg' : ' se cuenta en unidades'}</span>
            )}
          </span>
          <button
            type="button"
            className="prod-rm"
            aria-label={`Quitar ${name}`}
            onClick={() => setOutputs((current) => current.filter((x) => x.key !== o.key))}
          >
            ✕
          </button>
        </div>
        {o.loading ? <div className="prod-sub">Cargando…</div> : (
          <>
            {o.firstTime && (
              <div className="prod-ask">
                <div className="q">¿Cómo se cuenta en bodega? (primera vez)</div>
                <div className="prod-seg">
                  {[['un', 'Unidades'], ['kg', 'Kg']].map(([unit, label]) => (
                    <button
                      key={unit}
                      type="button"
                      className={o.unit === unit ? 'on' : ''}
                      aria-pressed={o.unit === unit}
                      onClick={() => patchOutput(o.key, { unit })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="prod-hint">
                  {o.suggested ? 'Sugerido según pedidos de Loken. Se guarda en el SKU.' : 'Se guarda en el SKU.'}
                </div>
              </div>
            )}
            {o.unit === 'un' && (
              <div className="prod-two">
                <div>
                  <div className="prod-lbl">Unidades</div>
                  <input
                    className="prod-input"
                    inputMode="numeric"
                    aria-label={`Unidades de ${name}`}
                    value={o.units}
                    onChange={(e) => patchOutput(o.key, { units: e.target.value })}
                  />
                </div>
                <div>
                  <div className="prod-lbl">Peso total (kg)</div>
                  <input
                    className="prod-input"
                    inputMode="decimal"
                    aria-label={`Peso total de ${name}`}
                    placeholder="kg"
                    value={o.weight}
                    onChange={(e) => patchOutput(o.key, { weight: e.target.value })}
                  />
                </div>
              </div>
            )}
            {o.unit === 'kg' && (
              <>
                <div className="prod-lbl">Peso total (entra a stock)</div>
                <input
                  className="prod-input"
                  inputMode="decimal"
                  aria-label={`Peso total de ${name}`}
                  placeholder="kg"
                  value={o.weight}
                  onChange={(e) => patchOutput(o.key, { weight: e.target.value })}
                />
              </>
            )}
            <div className="prod-two">
              <div>
                <div className="prod-lbl">Vence *</div>
                <input
                  type="date"
                  className="prod-input"
                  aria-label={`Vence de ${name}`}
                  required
                  min={isoDay(1)}
                  value={o.expiration}
                  onChange={(e) => patchOutput(o.key, { expiration: e.target.value })}
                />
              </div>
              <div>
                <div className="prod-lbl">Guardar en</div>
                <LocationField
                  label={`Guardar ${name} en`}
                  value={o.location}
                  onChange={(location) => patchOutput(o.key, { location })}
                />
              </div>
            </div>
            {o.unit === 'un' && badUnits(o.units) && <div className="prod-bad">{WHOLE_UNITS}</div>}
            {badLocation && <div className="prod-bad">{BAD_LOCATION}</div>}
          </>
        )}
      </div>
    );
  };

  const form = (
    <div>
      <div className="prod-lbl">Lo que salió</div>
      <label className="prod-check">
        <input type="checkbox" checked={noOutput} onChange={(e) => setNoOutput(e.target.checked)} />
        No salió producto
      </label>
      {noOutput ? (
        <label className="prod-lbl" style={{ display: 'block' }}>
          Motivo
          <textarea
            className="prod-input"
            rows={2}
            value={noOutputReason}
            onChange={(e) => setNoOutputReason(e.target.value)}
          />
        </label>
      ) : (
        <>
          {outputs.map(renderOutput)}
          {addingOutput ? (
            <ProduccionSkuPicker
              placeholder="Escanea o escribe el producto"
              excludeIds={outputs.map((o) => o.sku.id)}
              onSelect={addOutput}
            />
          ) : (
            <button type="button" className="prod-btn ghost prod-btn-small" onClick={() => setAddingOutput(true)}>
              + Agregar producto
            </button>
          )}
        </>
      )}

      <div className="prod-lbl" style={{ marginTop: 18 }}>Insumos usados</div>
      {supplies.map((s) => (
        <div className="prod-line" key={s.key}>
          <div className="prod-line-head">
            <b>{s.sku.name}</b>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="prod-input kg"
                inputMode="decimal"
                aria-label={`Cantidad de ${s.sku.name}`}
                placeholder="cantidad"
                value={s.qty}
                onChange={(e) => changeSupplyQty(s, e.target.value)}
              />
              <button
                type="button"
                className="prod-rm"
                aria-label={`Quitar insumo ${s.sku.name}`}
                onClick={() => removeSupply(s.key)}
              >
                ✕
              </button>
            </div>
          </div>
          <SupplyPreview supply={s} onStart={(place) => changeSupplyStart(s, place)} />
        </div>
      ))}
      {addingSupply ? (
        <ProduccionSkuPicker
          placeholder="Escanea o escribe el insumo"
          excludeIds={supplies.map((s) => s.sku.id)}
          onSelect={addSupply}
        />
      ) : (
        <button type="button" className="prod-btn ghost prod-btn-small" onClick={() => setAddingSupply(true)}>
          + Agregar insumo
        </button>
      )}

      <div className="prod-lbl" style={{ marginTop: 18 }}>Bolsa, interfolio y etiqueta</div>
      <div className="prod-sub" style={{ margin: 0 }}>Opcional. Solo información: no descuenta stock.</div>
      <div className="prod-counts">
        {COUNTS.map(({ key, label }) => (
          <label key={key} className="prod-count">
            <span>{label}</span>
            <input
              className="prod-input"
              inputMode="numeric"
              aria-label={label}
              value={counts[key]}
              onChange={(e) => setCounts((current) => ({ ...current, [key]: e.target.value }))}
            />
          </label>
        ))}
      </div>

      <div className="prod-lbl" style={{ marginTop: 18 }}>Devuelto</div>
      <div className="prod-sub" style={{ margin: 0 }}>kg que vuelven al rack</div>
      {raw.length === 0 && <div className="prod-sub">No se retiró materia prima.</div>}
      {raw.map((row) => {
        const problem = leftoverProblem(row, leftovers[row.sku.id]);
        return (
          <div className="prod-line" key={row.sku.id}>
            <div className="prod-line-head">
              <span>
                <b>{row.sku.name}</b>
                <div className="prod-sub" style={{ margin: 0 }}>{`Retirado ${formatKg(row.pulled)} kg`}</div>
              </span>
              <input
                className="prod-input kg"
                inputMode="decimal"
                aria-label={`Devuelto de ${row.sku.name}`}
                placeholder="kg"
                value={leftovers[row.sku.id]}
                onChange={(e) => setLeftovers((current) => ({ ...current, [row.sku.id]: e.target.value }))}
              />
            </div>
            {problem && <div className="prod-bad">{problem}</div>}
          </div>
        );
      })}
    </div>
  );

  const summary = (
    <>
      <div className="prod-lbl">Resumen</div>
      <div className={`prod-sum${mermaAlert ? ' amber' : ''}`}>
        <div className="r"><span>Entró</span><b>{`${formatKg(entered)} kg`}</b></div>
        <div className="r"><span>Salió</span><b>{`${formatKg(outKg)} kg`}</b></div>
        <div className="r loss"><span>{mermaText}</span></div>
      </div>
    </>
  );

  const ready = noOutput ? [] : outputs.filter((o) => !outputProblem(o, today));
  const stock = ready.length > 0 && (
    <>
      <div className="prod-lbl">Entra a stock</div>
      {ready.map((o) => (
        <div className="prod-card green" key={o.key}>
          <div className="n">
            {`${o.unit === 'un' ? `${formatKg(parseUnits(o.units))} un.` : `${formatKg(parseKg(o.weight))} kg`} · ${o.sku.name}`}
          </div>
          <div className="d">{`${expiry(o.expiration)} · en ${placeOf(locationOf(o.location))}`}</div>
        </div>
      ))}
    </>
  );

  const lots = madeWith(order.pulls);
  const trace = lots.length > 0 && (
    <>
      <div className="prod-lbl">Hecho con</div>
      <div className="prod-trace">
        {lots.map((row) => (
          <div className="row" key={row.key}>
            {`${row.name} · lote ${row.batch} (${expiry(row.date)}) · ${formatKg(row.kg)} kg`}
          </div>
        ))}
      </div>
    </>
  );

  // Nothing was used: the order can only be cancelled, from its detail.
  const noRaw = raw.length === 0 || (error && error.code === 'sin_materia_prima');
  const closing = (
    <>
      {error && <div className="prod-bad prod-error">{error.text}</div>}
      {noRaw && (
        <button type="button" className="prod-btn red prod-btn-small" onClick={() => navigate(`/production/${poNumber}`)}>
          Ver la orden para anularla
        </button>
      )}
      {pending ? (
        <div className="prod-confirm">
          <div className="prod-panel-q">{pending.message}</div>
          <label className="prod-check">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            Confirmo
          </label>
          <div className="prod-submit-row">
            <button type="button" className="prod-btn amber" disabled={!ack || busy} onClick={confirmAndClose}>
              Terminar de todas formas
            </button>
            <button type="button" className="prod-btn ghost" disabled={busy} onClick={dropPending}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div className="prod-submit-row">
          <button
            type="button"
            className="prod-btn green"
            disabled={Boolean(reason) || busy}
            onClick={() => submit(NO_FLAGS)}
          >
            {busy ? 'Terminando…' : 'Terminar producción'}
          </button>
          {reason && <span className="prod-sub" style={{ margin: 0 }}>{reason}</span>}
        </div>
      )}
    </>
  );

  return (
    <div className="prod-page">
      {header}
      {/* Nothing can change while the close is being sent. */}
      <fieldset className="prod-fieldset" disabled={busy}>
        {wide ? (
          <div className="prod-split">
            {form}
            <div className="prod-side">
              {summary}
              {closing}
              {stock}
              {trace}
            </div>
          </div>
        ) : (
          <>
            {form}
            {summary}
            {stock}
            {trace}
            {closing}
          </>
        )}
      </fieldset>
    </div>
  );
}

export default ProduccionTerminar;
